import React, { useEffect, useState } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Paper, Tooltip,
    ToggleButtonGroup, ToggleButton
} from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

interface Merchant {
    ap_x_label: string;
    ap_y_label: string;
    bin: string;
    merchant_name: string;
    balance_x: string;
    balance_y: string;
    turnover_from_x: string;
    turnover_from_y: string;
}

const formatAmount = (amount: string | number | null): string => {
    if (!amount) return '0.00';
    if (typeof amount === 'number') amount = amount.toString();
    amount = amount.replace(',', '.');
    const parts = amount.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const decimalPart = parts[1] ? parts[1].padEnd(2, '0') : '00';
    return `${integerPart}.${decimalPart}`;
};

type CurrencyRow = {
    label: string;
    balance: string;
    turnover_from: string;
};

function getMerchantCurrencies(merchant: Merchant): CurrencyRow[] {
    const rows: CurrencyRow[] = [];
    if (merchant.ap_x_label) {
        rows.push({
            label: merchant.ap_x_label,
            balance: merchant.balance_x,
            turnover_from: merchant.turnover_from_x,
        });
    }
    if (merchant.ap_y_label && merchant.ap_y_label !== merchant.ap_x_label) {
        rows.push({
            label: merchant.ap_y_label,
            balance: merchant.balance_y,
            turnover_from: merchant.turnover_from_y,
        });
    }
    return rows;
}

const MerchantRegistryPage: React.FC = () => {
    const [merchants, setMerchants] = useState<Merchant[]>([]);
    const [filterMode, setFilterMode] = useState<'all' | 'withBalance'>('all');

    const handleFilterChange = (_: React.MouseEvent<HTMLElement>, value: 'all' | 'withBalance' | null) => {
        if (value !== null) setFilterMode(value);
    };

    useEffect(() => {
        const fetchMerchants = async () => {
            const jwtToken = document.cookie
                .split('; ')
                .find((row) => row.startsWith('jwtToken='))?.split('=')[1];
            try {
                const response = await fetch('/api/merchant-registry/list', {
                    headers: {
                        Authorization: `Bearer ${jwtToken}`,
                        'Content-Type': 'application/json',
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setMerchants(data);
                } else {
                    console.error('Failed to fetch merchant registry:', response.status);
                }
            } catch (error) {
                console.error('Error fetching merchant registry:', error);
            }
        };

        fetchMerchants();
    }, []);

    /**
     * Helper function to build table rows with computed remainder
     */
    const buildRows = () => {
        return merchants.flatMap((merchant) => {
            const currencies = getMerchantCurrencies(merchant);
            return currencies.map((currency) => {
                const balanceNum = parseFloat(currency.balance || '0');
                const turnoverNum = parseFloat(currency.turnover_from || '0');
                const remainder = balanceNum - turnoverNum;

                return {
                    key: `${merchant.bin}-${currency.label}`,
                    bin: merchant.bin,
                    name: merchant.merchant_name,
                    label: currency.label,
                    balanceNum,
                    turnoverNum,
                    remainder,
                };
            });
        });
    };

    const rows = buildRows();
    const filteredRows = filterMode === 'withBalance' ? rows.filter((r) => r.remainder !== 0) : rows;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" fontWeight={700} mb={2}>
                Реестр мерчантов
            </Typography>

            {/* Toggle: All / With Balance */}
            <Box mb={2}>
                <ToggleButtonGroup
                    size="small"
                    value={filterMode}
                    exclusive
                    onChange={handleFilterChange}
                >
                    <ToggleButton value="all">Все</ToggleButton>
                    <ToggleButton value="withBalance">Есть остаток</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>BIN</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Название</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right"></TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                                Баланс
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                                <Tooltip title="Ушедшая сумма">
                                    <Box component="span" display="inline-flex" alignItems="center">
                                        Переведено&nbsp;
                                        <ArrowOutwardIcon fontSize="small" color="disabled" />
                                    </Box>
                                </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="left">
                                <Tooltip title="Неизрасходованная сумма на счете">
                                    <Box component="span" display="inline-flex" alignItems="center">
                                        Остаток&nbsp;
                                        <ArrowOutwardIcon fontSize="small" color="disabled" />
                                    </Box>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.filter((x) => x.balanceNum > 0).map((row) => (
                            <TableRow key={row.key}>
                                <TableCell>{row.bin}</TableCell>
                                <TableCell>{row.name}</TableCell>
                                <TableCell align="right">{row.label}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 500 }}>
                                    {formatAmount(row.balanceNum.toString())}
                                </TableCell>
                                <TableCell align="right">
                                    <span style={{ color: '#222' }}>
                                        {formatAmount(row.turnoverNum.toString())}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    {formatAmount(row.remainder.toString())}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default MerchantRegistryPage;
